import { Box, CardContent, Skeleton, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { Card, Text } from "@src/appV2/redesign/system/ui-components";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { Reaction } from "@src/appV2/redesign/Workplace/Review/types";
import { useCallback, useRef, useState } from "react";

import { PEER_REVIEW_CARD_HEIGHT, PeerReviewCard } from "./PeerReviewCard";
import { useSwipeGesture } from "./useSwipeGesture";

interface PeerReviewReaction {
  commentId: string;
  reaction: Reaction;
}

interface PeerReviewSwipeDeckProps {
  comments: WorkplaceCommentType[];
  workplaceId: string;
  onComplete: (reactions: readonly PeerReviewReaction[]) => void;
  onSkip: (reactions: readonly PeerReviewReaction[]) => void;
}

const CARD_STACK_SCALES = [1, 0.95, 0.9] as const;
const CARD_STACK_OFFSETS = [0, 16, 32] as const;
const OVERLAY_OPACITY_DIVISOR = 100;
const OVERLAY_MAX_OPACITY = 0.8;

export function PeerReviewSwipeDeck(props: PeerReviewSwipeDeckProps) {
  const { comments, workplaceId, onComplete, onSkip } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeckCompleted, setIsDeckCompleted] = useState(false);
  const reactionsRef = useRef<PeerReviewReaction[]>([]);

  const logInteraction = useCallback(
    (action: string, includeCommentId = true) => {
      logEvent(APP_V2_USER_EVENTS.PEER_REVIEW_INTERACTION, {
        action,
        ...(includeCommentId && { commentId: comments[currentIndex]?.id }),
        cardIndex: currentIndex,
        totalCards: comments.length,
        workplaceId,
      });
    },
    [currentIndex, comments, workplaceId]
  );

  function recordReaction(reaction: Reaction) {
    const comment = comments[currentIndex];
    if (!comment) {
      return;
    }

    const newReaction: PeerReviewReaction = { commentId: comment.id, reaction };
    reactionsRef.current = [...reactionsRef.current, newReaction];

    const nextIndex = currentIndex + 1;
    if (nextIndex >= comments.length) {
      setIsDeckCompleted(true);
      onComplete(reactionsRef.current);
    } else {
      setCurrentIndex(nextIndex);
    }
  }

  function handleSwipeLeft(source?: { fromTap?: boolean }) {
    if (!source?.fromTap) {
      logInteraction("swipe_dislike");
    }

    recordReaction(Reaction.DISLIKE);
  }

  function handleSwipeRight(source?: { fromTap?: boolean }) {
    if (!source?.fromTap) {
      logInteraction("swipe_like");
    }

    recordReaction(Reaction.LIKE);
  }

  const { cardRef, style, isAnimatingExit, deltaX, triggerSwipe } = useSwipeGesture({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    isEnabled: currentIndex < comments.length,
    cardKey: currentIndex,
  });

  const handleTapDislike = useCallback(() => {
    logInteraction("tap_dislike");
    triggerSwipe("left", { fromTap: true });
  }, [logInteraction, triggerSwipe]);

  const handleTapLike = useCallback(() => {
    logInteraction("tap_like");
    triggerSwipe("right", { fromTap: true });
  }, [logInteraction, triggerSwipe]);

  const handleSkip = useCallback(() => {
    if (isAnimatingExit) {
      return;
    }

    logInteraction("skip", false);
    onSkip(reactionsRef.current);
  }, [isAnimatingExit, logInteraction, onSkip]);

  if (isDeckCompleted) {
    return (
      <Stack spacing={5} alignItems="center" sx={{ flex: 1 }}>
        <Card
          variant="secondary"
          sx={{ borderRadius: 4, width: "100%", minHeight: PEER_REVIEW_CARD_HEIGHT }}
        >
          <CardContent sx={{ padding: 7 }}>
            <Stack spacing={4}>
              <Stack direction="row" spacing={3} alignItems="flex-start">
                <Skeleton variant="circular" width={32} height={32} />
                <Stack sx={{ flex: 1 }} direction="row" justifyContent="space-between">
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton variant="text" width={70} height={16} />
                </Stack>
              </Stack>
              <Skeleton variant="text" width={100} height={20} />
              <Stack spacing={1}>
                <Skeleton variant="text" width="100%" height={18} />
                <Skeleton variant="text" width="80%" height={18} />
                <Skeleton variant="text" width="60%" height={18} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack spacing={5} alignItems="center" sx={{ flex: 1 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "20rem",
          flex: 1,
        }}
      >
        {comments.map((comment, index) => {
          const relativeIndex = index - currentIndex;

          if (relativeIndex < 0 || relativeIndex > 2) {
            return null;
          }

          const isCurrentCard = relativeIndex === 0;
          const scale = CARD_STACK_SCALES[relativeIndex] ?? 0.9;
          const offsetY = CARD_STACK_OFFSETS[relativeIndex] ?? 16;

          return (
            <Box
              key={comment.id}
              ref={isCurrentCard ? cardRef : undefined}
              style={isCurrentCard ? style : undefined}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                zIndex: 3 - relativeIndex,
                ...(!isCurrentCard && {
                  transform: `scale(${scale}) translateY(${offsetY}px)`,
                  transition: "transform 500ms ease-out",
                  pointerEvents: "none",
                }),
              }}
            >
              <PeerReviewCard comment={comment} />
              {isCurrentCard && deltaX !== 0 && (
                <Box
                  sx={(theme) => ({
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: 4,
                    backgroundColor: theme.palette.primary.main,
                    opacity: Math.min(
                      Math.abs(deltaX) / OVERLAY_OPACITY_DIVISOR,
                      OVERLAY_MAX_OPACITY
                    ),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  })}
                >
                  <Text bold variant="h4" sx={{ color: "common.white" }}>
                    {deltaX > 0 ? "Like 👍" : "Dislike 👎"}
                  </Text>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <Text variant="body2">
        {currentIndex + 1} / {comments.length}
      </Text>

      <Stack direction="row" spacing={6} justifyContent="center" alignItems="center">
        <Stack alignItems="center" spacing={2}>
          <IconButton
            aria-label="Dislike"
            size="large"
            variant="outlined"
            iconType="close"
            disabled={isAnimatingExit}
            onClick={handleTapDislike}
          />
          <Text variant="caption">Dislike</Text>
        </Stack>
        <Stack alignItems="center" spacing={2}>
          <IconButton
            aria-label="Like"
            size="large"
            variant="outlined"
            iconType="checkmark"
            disabled={isAnimatingExit}
            onClick={handleTapLike}
          />
          <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
            Like
          </Text>
        </Stack>
      </Stack>

      <TextButton sx={{ alignSelf: "center" }} disabled={isAnimatingExit} onClick={handleSkip}>
        Skip
      </TextButton>
    </Stack>
  );
}
